/**
 * Workspace, collaboration, settings, agent memory and auth events.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const WORKSPACE_EVENTS = {
  // Workspace
  WORKSPACE_SWITCHED: 'Workspace Switched',
  WORKSPACE_CREATED: 'Workspace Created',
  // Self-serve workspace creation is paused; the disabled "Create Workspace"
  // button fires this event so we can quantify demand and learn use cases
  // before re-opening the flow. Properties: source ('settings' | 'switcher'),
  // existing_workspace_count.
  WORKSPACE_CREATE_BLOCKED: 'Workspace Create Blocked',
  // Fires when the WorkspaceContext detects the pinned workspaceId
  // isn't in the user's memberships and auto-switches to a valid
  // workspace. A spike here signals the workspace-removal-lockout
  // bug class is recurring (incident 2026-05-13). Properties:
  // from_workspace_id, to_workspace_id, available_count.
  WORKSPACE_AUTO_RECOVERED: 'Workspace Auto Recovered',
  // Workspace-level research context (field / lab size) edited in Settings.
  // Properties: fields_changed (enum field NAMES only — never the free-text
  // *Other values, which are UGC).
  WORKSPACE_RESEARCH_CONTEXT_UPDATED: 'Workspace Research Context Updated',
  // Workspace logo set or cleared in Settings, on the server's success.
  // Properties: was_replaced (boolean), edited (boolean: the admin cropped,
  // zoomed or rotated before saving), source_type (closed enum of mime types),
  // from_current (boolean: reopened the current logo instead of a new file).
  WORKSPACE_LOGO_UPLOADED: 'Workspace Logo Uploaded',
  WORKSPACE_LOGO_REMOVED: 'Workspace Logo Removed',
  // The workspace menu opened. Properties: source ('nav_rail': the desktop
  // rail's tile; 'mobile_workspace': the phone Workspace page's title).
  WORKSPACE_MENU_OPENED: 'Workspace Menu Opened',

  // Collaboration
  INVITATION_CREATED: 'Invitation Created',
  INVITATION_LINK_COPIED: 'Invitation Link Copied',
  INVITATION_ACCEPTED: 'Invitation Accepted',
  INVITATION_REVOKED: 'Invitation Revoked',
  // Differentiated from INVITATION_CREATED so the trial → paid funnel
  // has its own conversion-relevant event. Fires on the FE after a
  // successful Stripe quantity bump + paid invitation row creation.
  SEAT_PURCHASED: 'Seat Purchased',
  // Free guest invite — distinct from paid seats because guests don't
  // affect Stripe billing or seat caps the same way.
  GUEST_INVITED: 'Guest Invited',
  MEMBER_ROLE_CHANGED: 'Member Role Changed',
  MEMBER_REMOVED: 'Member Removed',

  // Settings
  THEME_CHANGED: 'Theme Changed',
  PROFILE_UPDATED: 'Profile Updated',
  // ORCID — research-identity flow on Profile + Onboarding. Properties
  // intentionally do NOT include the iD or the resolved name (PII).
  ORCID_VERIFIED: 'ORCID Verified',
  ORCID_SAVED: 'ORCID Saved',
  ORCID_REMOVED: 'ORCID Removed',

  // Agent Memory
  MEMORY_EDITED: 'Memory Edited',
  MEMORY_SAVE_REJECTED: 'Memory Save Rejected',
  // Explicit memory ("What Bower remembers") — view/delete transparency surface.
  // No memory content (UGC) or ids in properties; count only.
  MEMORY_VIEWED: 'Memory Viewed',
  MEMORY_DELETED: 'Memory Deleted',

  // Marketing / lifecycle email
  EMAIL_CTA_CLICKED: 'Email CTA Clicked',

  // Auth
  USER_SIGNED_UP: 'User Signed Up',
  EMAIL_VERIFIED: 'Email Verified',
  VERIFICATION_EMAIL_SEND_REQUESTED: 'Verification Email Send Requested',
  VERIFICATION_EMAIL_SEND_SUCCEEDED: 'Verification Email Send Succeeded',
  VERIFICATION_EMAIL_SEND_FAILED: 'Verification Email Send Failed',
  VERIFICATION_PROMPT_MANUAL_CONTINUE: 'Verification Prompt Manual Continue',
  // Firebase recoverEmail flow — reverting an email change from the security
  // email. Account-compromise recovery signal; captured client-side because
  // the flow is pure Firebase (no backend round-trip to capture server-side).
  RECOVER_EMAIL_COMPLETED: 'Recover Email Completed',
  RECOVER_EMAIL_FAILED: 'Recover Email Failed',
  USER_SIGNED_IN: 'User Signed In',
  USER_SIGNED_OUT: 'User Signed Out',
  // Client-side SSO failure with the Firebase error code (`error_code`).
  // Captured for EVERY failed popup attempt — including "popup closed",
  // which also fires when the post-auth handoff to the opener dies (stale
  // bundle / partitioned storage), not just genuine user cancels. The
  // server only sees successes, so this is the sole visibility into
  // sign-in friction. `during` ∈ 'initial' | 'link_recovery'.
  SSO_SIGN_IN_FAILED: 'SSO Sign In Failed',

  // Settings → Workspace group navigation entries (equipment/audit/trash).
  // Properties: page.
  SETTINGS_WORKSPACE_PAGE_OPENED: 'Settings Workspace Page Opened',
  // In-page settings search — a result was chosen. PII-safe: carries the static
  // result id + kind (tab|field) only, never the typed query. Properties:
  // result_id, result_kind.
  SETTINGS_SEARCH_RESULT_SELECTED: 'Settings Search Result Selected',

  // Workspace Canvas (the folders UI).
  // Every event here is IDs/enums/counts only, never an item, folder, tag or
  // person name (rules/analytics.md). Count properties use the shared
  // `countBucket` helper (`utils/analyticsCountBucket.ts`).

  // CanvasHeader's toolbar. Properties: view ('folders'|'cards'|'list').
  WORKSPACE_VIEW_SWITCHED: 'Workspace View Switched',
  // Properties: scope ('children'|'descendants').
  WORKSPACE_SCOPE_CHANGED: 'Workspace Scope Changed',
  // One event for all four filter menus (Type/Modified/People/Tags), told
  // apart by `filter`. Properties: filter ('type'|'people'|'tags'|'modified'),
  // value_count_bucket (the chosen option count; '0' when cleared).
  WORKSPACE_FILTER_CHANGED: 'Workspace Filter Changed',
  // Properties: sort (WorkspaceItemsSort), order ('asc'|'desc').
  WORKSPACE_SORT_CHANGED: 'Workspace Sort Changed',
  // The grid views' "Show all" / "Show less" on a long collections section.
  // Properties: expanded (boolean), collection_count_bucket.
  WORKSPACE_COLLECTIONS_TOGGLED: 'Workspace Collections Toggled',

  // ItemContextMenu (tree, tile, card and row all open the same menu, the
  // top bar's ⋮ opens it on the open collection, and a detail page's ⋮ on
  // that item). Properties: surface
  // ('tree'|'tile'|'card'|'row'|'topbar'|'detail'), trigger
  // ('pointer'|'keyboard'|'longpress'), target_count_bucket.
  ITEM_CONTEXT_MENU_OPENED: 'Item Context Menu Opened',
  // An entry chosen from it. Properties: action (ItemMenuActionId); surface
  // ('topbar' for a collection's ⋮, 'detail' for a detail page's ⋮) only
  // when chosen from one of those; option (a submenu choice id such as 'pdf')
  // only for a choice inside an entry's submenu.
  ITEM_CONTEXT_ACTION: 'Item Context Action',

  // A click on an ActionBar action (inline or in its More menu) only; the
  // context menu reports ITEM_CONTEXT_ACTION, and the Delete/F2 keys report
  // neither. Properties: action (SelectionActionId), count_bucket,
  // disabled_reason (a CODE from resolveSelectionActions' `reasonCode` —
  // never the reason sentence, which names an item) present only when the
  // click landed on a disabled action.
  SELECTION_ACTION: 'Selection Action',
  // A marquee (rubber-band) selection released in the Folder or Card view,
  // once per drag. Properties: count_bucket (items selected after it),
  // additive (Shift or Cmd/Ctrl held, so it added to the selection).
  MARQUEE_SELECTED: 'Marquee Selected',
  // An in-place rename saved (InlineRenameField; F2, the menu's or the bar's
  // Rename start it). Properties: surface ('canvas'|'tree'), kind
  // ('folder'|'file').
  WORKSPACE_ITEM_RENAMED: 'Workspace Item Renamed',
  // An item opened from the workspace: { surface, view, item_kind }. IDs and enums only.
  WORKSPACE_ITEM_OPENED: 'Workspace Item Opened',

  // A desktop file drop accepted and handed to the import flow. Properties:
  // file_count_bucket, target ('canvas'|'folder'), source ('drop'|'paste').
  DROP_UPLOAD_STARTED: 'Drop Upload Started',

  // Files accepted by the shared file intake (a drop zone or drop overlay), on
  // any surface. Properties: surface (IntakeSurface), source
  // ('drop'|'paste'|'picker'|'camera'), file_count_bucket, skipped_count,
  // rejected_count. Counts and closed enums only, never a file name.
  FILE_INTAKE_ACCEPTED: 'File Intake Accepted',

  // An action on a full-page empty state (components/ui/EmptyState). Properties:
  // action ('clear_filters'|'include_subfolders'|'import'|'add'|'ask_bower'|
  // 'add_equipment'|'add_protocol'|'record_meeting'|'upload_recording'|
  // 'glasses_guide'|'phone_qr'|'add_papers'), surface ('workspace'|'equipment'|
  // 'protocol_library'|'graph'|'trash', or a suggested collection's splash
  // (decision 0018): 'suggested_protocols'|'suggested_meetings'|
  // 'suggested_live_sessions'|'suggested_equipment'|'suggested_literature').
  EMPTY_STATE_ACTION: 'Empty State Action',

  // The one-time "Set up suggested collections" offer, at the point of
  // success. Properties: action ('setup'|'dismiss'), created_count,
  // skipped_count, failed_count.
  PURPOSE_COLLECTIONS_OFFER_CLOSED: 'Purpose Collections Offer Closed',

  // DataGrid (the List view). Properties: change ('hide'|'show'|'reorder'|
  // 'resize'), grid_id.
  DATA_GRID_COLUMN_CHANGED: 'Data Grid Column Changed',

  // A bulk move (action bar, context menu or drag-drop) committed. Properties:
  // count_bucket, target_kind ('workspace'|'project').
  WORKSPACE_ITEMS_MOVED: 'Workspace Items Moved',
  // The canvas clipboard (Cmd/Ctrl+C, X, V; an app clipboard, never the
  // system one). Copied and Cut fire when the selection is put on it;
  // Properties: count_bucket.
  WORKSPACE_ITEMS_CLIPBOARD_COPIED: 'Workspace Items Clipboard Copied',
  WORKSPACE_ITEMS_CLIPBOARD_CUT: 'Workspace Items Clipboard Cut',
  // A paste that landed: a copy (the "Copy to..." copy, succeeded items) or
  // a cut (the bulk move, moved items; WORKSPACE_ITEMS_MOVED fires too).
  // Properties: mode ('copy'|'cut'), count_bucket, target_kind
  // ('workspace'|'project'), trigger ('keyboard'|'menu').
  WORKSPACE_ITEMS_PASTED: 'Workspace Items Pasted',
  // An owner or admin turned showing capture locations (photo GPS) on or off
  // in Workspace settings. Properties: enabled.
  CAPTURE_LOCATION_SETTING_CHANGED: 'Capture Location Setting Changed',
} as const;
